/**
 * Screening outcome — the hit rating a report carries and what it rests on.
 *
 * Wire shapes mirror `vnext/schemas/screening_outcome.py`. The rating is
 * the worst of the report's subjects; each subject's rating is the worst of
 * its retained findings under the risk profile the report resolves to.
 */

export type ScreeningRating = 'clear' | 'potential' | 'hit';

/** Every rating, worst first — the order filters and tallies are shown in. */
export const SCREENING_RATINGS: readonly ScreeningRating[] = ['hit', 'potential', 'clear'];

export const SCREENING_RATING_META: Record<
  ScreeningRating,
  { label: string; color: string; description: string }
> = {
  hit: {
    label: 'Hit',
    color: 'red',
    description: 'A confirmed finding that needs attention.',
  },
  potential: {
    label: 'Potential',
    color: 'yellow',
    description: 'Something was retained but did not meet the hit threshold.',
  },
  clear: {
    label: 'Clear',
    color: 'green',
    description: 'Remediation retained nothing that rates.',
  },
};

/** The `rating` list-filter value for reports not rated yet. */
export const UNRATED_FILTER = 'unrated';

/** Select options for a rating filter, worst first. */
export const SCREENING_RATING_FILTER_OPTIONS = SCREENING_RATINGS.map((rating) => ({
  value: rating,
  label: SCREENING_RATING_META[rating].label,
}));

/** The extra option admin filters offer: reports with no rating yet. */
export const UNRATED_FILTER_OPTION = { value: UNRATED_FILTER, label: 'Not rated yet' };

export type FindingKind =
  | 'sanctions'
  | 'pep'
  | 'watchlist'
  | 'adverse_media'
  | 'corporate'
  | 'bankruptcy'
  | 'other';

export const FINDING_KIND_LABELS: Record<FindingKind, string> = {
  sanctions: 'Sanctions',
  pep: 'PEP',
  watchlist: 'Watchlist',
  adverse_media: 'Adverse media',
  corporate: 'Corporate record',
  bankruptcy: 'Bankruptcy',
  other: 'Other',
};

export type FindingSeverity = 'high' | 'medium' | 'low' | 'unknown';

export const FINDING_SEVERITY_LABELS: Record<FindingSeverity, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  unknown: 'Unassessed',
};

/** Structured breakdown stored on the subject outcome. */
export interface ScreeningCounts {
  total: number;
  by_rating: Partial<Record<ScreeningRating, number>>;
  by_kind: Partial<Record<FindingKind, number>>;
  by_severity: Partial<Record<FindingSeverity, number>>;
}

/** One-line summary of a breakdown: "1 sanctions · 2 adverse media (1 high)". */
export function summariseScreeningCounts(counts: ScreeningCounts | null | undefined): string {
  if (!counts || !counts.total) {
    return 'No retained findings';
  }
  const parts = Object.entries(counts.by_kind ?? {})
    .filter(([, n]) => (n ?? 0) > 0)
    .map(([kind, n]) => {
      const label = FINDING_KIND_LABELS[kind as FindingKind] ?? kind;
      if (kind === 'adverse_media') {
        const high = counts.by_severity?.high ?? 0;
        return `${n} ${label.toLowerCase()}${high ? ` (${high} high)` : ''}`;
      }
      return `${n} ${label.toLowerCase()}`;
    });
  return parts.join(' · ');
}

export interface ApiScreeningFinding {
  id: string;
  subject_id: string;
  kind: FindingKind;
  category: string | null;
  category_label: string;
  severity: FindingSeverity | null;
  match_confidence: number | null;
  decided_by: 'llm' | 'deterministic' | 'analyst' | 'prior_run' | 'unknown';
  provider: string;
  check_type: string;
  list_source: string | null;
  list_category: string | null;
  label: string | null;
  source_url: string | null;
  occurred_at: string | null;
  details: Record<string, unknown>;
  raw_data_hash: string;
  remediation_run_prefix: string;
  rating: ScreeningRating;
  rule_index: number | null;
  created_at: string | null;
}

export interface ScreeningFinding {
  id: string;
  subjectId: string;
  kind: FindingKind;
  category: string | null;
  categoryLabel: string;
  severity: FindingSeverity | null;
  matchConfidence: number | null;
  decidedBy: ApiScreeningFinding['decided_by'];
  provider: string;
  checkType: string;
  listSource: string | null;
  listCategory: string | null;
  label: string | null;
  sourceUrl: string | null;
  occurredAt: string | null;
  details: Record<string, unknown>;
  rating: ScreeningRating;
  ruleIndex: number | null;
}

export interface ApiSubjectScreeningOutcome {
  subject_id: string;
  subject_name: string | null;
  rating: ScreeningRating;
  counts: ScreeningCounts;
  coverage: { completed?: string[]; failed?: string[] };
  pending_review_count: number;
  provisional: boolean;
  high_risk_jurisdictions?: string[];
  profile_source: Record<string, unknown>;
  remediation_run_prefix: string;
  evaluated_at: string | null;
  /** Admin-only; null on the client wire. */
  findings: ApiScreeningFinding[] | null;
}

export interface SubjectScreeningOutcome {
  subjectId: string;
  subjectName: string | null;
  rating: ScreeningRating;
  counts: ScreeningCounts;
  coverage: { completed: string[]; failed: string[] };
  pendingReviewCount: number;
  provisional: boolean;
  /** The subject's jurisdictions on the profile's high-risk list — the HRJ
   * flag, independent of the rating. */
  highRiskJurisdictions: string[];
  profileSource: Record<string, unknown>;
  evaluatedAt: string | null;
  findings: ScreeningFinding[] | null;
}

export interface ApiReportScreeningOutcome {
  report_id: string;
  screening_rating: ScreeningRating | null;
  screening_rating_at: string | null;
  subjects: ApiSubjectScreeningOutcome[];
}

export interface ReportScreeningOutcome {
  reportId: string;
  screeningRating: ScreeningRating | null;
  screeningRatingAt: string | null;
  subjects: SubjectScreeningOutcome[];
}

export function transformToScreeningFinding(api: ApiScreeningFinding): ScreeningFinding {
  return {
    id: api.id,
    subjectId: api.subject_id,
    kind: api.kind,
    category: api.category,
    categoryLabel: api.category_label,
    severity: api.severity,
    matchConfidence: api.match_confidence,
    decidedBy: api.decided_by,
    provider: api.provider,
    checkType: api.check_type,
    listSource: api.list_source,
    listCategory: api.list_category,
    label: api.label,
    sourceUrl: api.source_url,
    occurredAt: api.occurred_at,
    details: api.details ?? {},
    rating: api.rating,
    ruleIndex: api.rule_index,
  };
}

export function transformToReportScreeningOutcome(
  api: ApiReportScreeningOutcome
): ReportScreeningOutcome {
  return {
    reportId: api.report_id,
    screeningRating: api.screening_rating,
    screeningRatingAt: api.screening_rating_at,
    subjects: api.subjects.map((s) => ({
      subjectId: s.subject_id,
      subjectName: s.subject_name,
      rating: s.rating,
      counts: s.counts,
      coverage: {
        completed: s.coverage?.completed ?? [],
        failed: s.coverage?.failed ?? [],
      },
      pendingReviewCount: s.pending_review_count,
      provisional: s.provisional,
      highRiskJurisdictions: s.high_risk_jurisdictions ?? [],
      profileSource: s.profile_source ?? {},
      evaluatedAt: s.evaluated_at,
      findings: s.findings ? s.findings.map(transformToScreeningFinding) : null,
    })),
  };
}
